import { IconPlus, IconSearch } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { CreatePublisherDialog } from "@/components/forms/publishers/create";
import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import {
	DataTableGeneric,
	type DataTableSearchParams,
	DataTableSearchSchema,
} from "@/components/ui/data-table-generic";
import { Input } from "@/components/ui/input";
import { orpc } from "@/orpc/client";
import { columns } from "@/tables/publishers";

export const Route = createFileRoute("/_app/publishers/")({
	component: Page,
	validateSearch: (search) => DataTableSearchSchema.parse(search),
	async loader({ context, preload }) {
		if (!preload) {
			return;
		}

		context.queryClient.prefetchQuery(
			orpc.publishers.list.queryOptions({
				input: {},
			}),
		);
	},
});

function Page() {
	const search = Route.useSearch();
	const navigate = Route.useNavigate();

	const { data, isFetching, refetch } = useQuery(
		orpc.publishers.list.queryOptions({
			input: {},
		}),
	);

	const [globalFilter, setGlobalFilter] = useState(search.q ?? "");

	const handleNavigate = useCallback(
		(params: DataTableSearchParams) => {
			navigate({
				replace: true,
				search: (prev) => ({
					...prev,
					...params,
				}),
			});
		},
		[navigate],
	);

	const toolbar = (
		<>
			<div className="relative">
				<Input
					value={globalFilter}
					onChange={(e) => {
						setGlobalFilter(e.target.value);
					}}
					placeholder="Search Publishers..."
					className="pl-10"
				/>
				<div className="pointer-events-none absolute top-0 left-2 flex h-full items-center justify-center">
					<IconSearch className="h-4" />
				</div>
			</div>
			<CreatePublisherDialog>
				<Button>
					<IconPlus />
					Create a Publisher
				</Button>
			</CreatePublisherDialog>
		</>
	);

	return (
		<>
			<SiteHeader breadcrumbs={[{ key: "publishers", label: "Publishers" }]} />
			<div className="flex flex-1 flex-col">
				<div className="@container/main flex flex-1 flex-col gap-2">
					<div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
						<DataTableGeneric
							columns={columns}
							data={data ?? []}
							isFetching={isFetching}
							handleReload={() => refetch()}
							toolbar={toolbar}
							searchParams={{
								sort: "ID:desc",
								...search,
								q: globalFilter,
							}}
							onNavigate={handleNavigate}
							getRowId={(row) => row.ID.toString()}
							config={{
								defaultPageSize: 25,
								pageSizeOptions: [25, 50, 100, 500],
							}}
						/>
					</div>
				</div>
			</div>
		</>
	);
}
